import { Component, Inject, isDevMode, LOCALE_ID, OnDestroy, OnInit, SecurityContext } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, Params } from '@angular/router';
import {Observable, Subscription} from 'rxjs';
import { filter } from 'rxjs/operators';
import { UserWrapper } from './models/user';
import { AuthService } from './services/auth.service';
import { CookieService } from 'ngx-cookie-service';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { CookieConsentSheetComponent } from './modules/shared/cookie-consent/cookie-consent.component';
import { ChatroomService } from "./services/chatroom.service";
import { UserService } from './services/user.service';
import { DomSanitizer, Meta } from '@angular/platform-browser';
import { BrowserAgent } from '@newrelic/browser-agent';
import { environment } from 'src/environments/environment';
import { AgentOptions } from '@newrelic/browser-agent/src/loaders/agent.js';
import { CookieNames } from './modules/shared/constants';
import { ToastrService } from 'ngx-toastr';
import { ScriptInjectionService } from './services/script.service';
import { MatomoTracker } from 'ngx-matomo-client';
import { CookieConsentClass } from './modules/shared/models/cookie-consent';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  currentLocaleLabel!: string;
  isHomePage: boolean = true;
  subscriberEmail?: string | undefined;
  subscribed: boolean = false;

  user$: Observable<UserWrapper>;
  userId: string | null = null;

  isLoading: boolean = true;
  newRelicInitialized: boolean = false;

  private readonly UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

  public readonly localesList = [
    { locale: 'en-GB', label: 'English', localeLabel: 'EN', ogLocale: 'en_GB'},
    { locale: 'fr', label: 'Français', localeLabel: 'FR', ogLocale: 'fr' },
    //{locale: 'de', label: 'Deutsch', localeLabel: 'DE'},
    //{locale: 'uk', label: 'Українська', localeLabel: 'UA'}
  ]

  editableRides: any[] = [];
  hasNewMessages: boolean = false;
  private newMessages$: Subscription;
  private chatroomPollingIntervalHandle: any;
  private userLoaded$: Subscription;

  constructor(private authService: AuthService,
              private router: Router,
              private chatRoom: ChatroomService,
              private userService: UserService,
              private cookieService: CookieService,
              @Inject(LOCALE_ID) public locale: string,
              private toastrService: ToastrService,
              private meta : Meta,
              private scriptInjectionService: ScriptInjectionService,
              private route: ActivatedRoute,
              private mbs: MatBottomSheet,
              private matomoTracker: MatomoTracker,
              private sanitizer: DomSanitizer) {
    this.user$ = this.authService.user$;
    this.userLoaded$ = this.user$.subscribe(user => {
      if (user.loggedIn) {
        this.onLogin(user);
      }
    });

    this.newMessages$ = this.chatRoom.newMessages.subscribe(newMessage => {
      this.hasNewMessages = newMessage;
    });

    this.setLocaleLabel();

    this.addSharedMetaTags();
    this.addMetaTagsForLocale(this.locale); 
  }

  ngOnInit(): void {
    const cookieConsentResults = this.getConsentState();

    this.router.events.pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Route resolution is complete
        this.isHomePage = event.url.startsWith('/home') 
          || event.url === '/'
          || event.url.startsWith('/?'); // home page or root path with or without query params

        // Process UTM params after navigation
        const currentParams = this.route.snapshot.queryParams;
        this.processUtmParams(currentParams);

        this.checkCookieConsent();
      });

    if (cookieConsentResults?.timestamp != null) {
      this.trySetupNewRelic();
    }
  }

  ngOnDestroy(): void {
    if (this.newMessages$) {
      this.newMessages$.unsubscribe();
    }
    this.userLoaded$.unsubscribe();
  }

  private onLogin(user : UserWrapper) {
    this.startPollingChat();
    this.userId = user.user?.id || null;
    let newrelic = (window as any).newrelic;
    if (newrelic && newrelic.setUserId) {
      newrelic.setUserId(this.userId);
    }
    if (this.userId) {
      this.matomoTracker.setUserId(this.userId);
    }
  }

  private startPollingChat() {
      // Poll every 120 seconds, Backend response cached for 2 minutes
      this.chatroomPollingIntervalHandle = setInterval(() => {
        this.chatRoom.checkForNewMessages();
      }, 120000);
  }

  public logout() {
    clearInterval(this.chatroomPollingIntervalHandle);
    this.authService.logout();
    let newrelic = (window as any).newrelic;
    if (newrelic && newrelic.setUserId) {
      newrelic.pauseReplay();
      newrelic.setUserId(null);
      this.matomoTracker.resetUserId();
    }
    this.router.navigateByUrl('/home')
  }

  onMessageClicked(): void{
    this.hasNewMessages = false;
  }

  public subscribe() {
    if (this.subscriberEmail != undefined && this.subscriberEmail.length > 0) {
      this.authService.subscribe(this.subscriberEmail).subscribe(_ => {
        this.subscribed = true;
      });
    }
  }

  public changeLocale(locale: string): void {
    if (locale === this.cookieService.get(CookieNames.locale)) {
      // No change in locale, do nothing
      return;
    }

    const cookieConsentResults = this.getConsentState();
    if (!cookieConsentResults.functional) {
      this.toastrService.warning($localize`Functional cookies are required to change the language.`);
      this.matomoTracker.trackEvent('Cookie Consent', 'Functional cookies required', 'Change language');
      this.showCookieModal();
      return;
    }

    this.matomoTracker.trackEvent('User', 'Locale Change', locale);
    this.userService.setLanguage(locale).subscribe(() => {
      let delocalisedPath = location.pathname.split('/').slice(2).join('/');
      window.location.href = `/${locale}/${delocalisedPath}`;
    });
  }

  protected readonly isDevMode = isDevMode;

  private processUtmParams(params: Params): void {
    this.UTM_PARAMS.forEach(key => {
      if (params[key]) {
        const sanitizedValue = this.sanitizer.sanitize(SecurityContext.HTML, params[key]); // Sanitize to prevent XSS
        if (sanitizedValue !== null) {
          localStorage.setItem(key, sanitizedValue);
        }
      }
    });
  }

  private getConsentState(): CookieConsentClass {
    let contentCookie = this.cookieService.get(CookieNames.consent);
    let cookieConsentResults = JSON.parse(contentCookie || '{}') as CookieConsentClass;
    return cookieConsentResults;
  }

  private trySetupNewRelic() {
    if (this.newRelicInitialized) {
      return; // New Relic already initialized
    }

    this.newRelicInitialized = true;

    if (window.location.hostname === 'localhost') {
      return;
    }

    let cookieConsentResults = this.getConsentState();
    if (!cookieConsentResults.statistics) {
      return;
    }

    const options : AgentOptions = {
      init: {
        session_replay: {
            enabled: true,
            block_selector: '',
            mask_text_selector: '',
            mask_all_inputs: true,
            collect_fonts: false,
            inline_images: false,
            fix_stylesheets: true,
            preload: false,
            mask_input_options: {}
        },
        distributed_tracing: { enabled: true },
        privacy: { cookies_enabled: true },
        ajax: environment.newRelic.ajax
      }, // NREUM.init
      info: {
        beacon: environment.newRelic.info.beacon,
        errorBeacon: environment.newRelic.info.errorBeacon,
        licenseKey: environment.newRelic.licenseKey,
        applicationID: environment.newRelic.applicationID,
        sa: 1
      }, // NREUM.info
      loader_config: {
        accountID: environment.newRelic.accountID,
        trustKey: environment.newRelic.trustKey,
        agentID: environment.newRelic.agentID,
        licenseKey: environment.newRelic.licenseKey,
        applicationID: environment.newRelic.applicationID
      } // NREUM.loader_config
    }

    // The agent loader code executes immediately on instantiation.
    const agent = new BrowserAgent(options);
    agent.setUserId(this.userId || null);
    agent.recordReplay();
    console.log('New Relic initialized');
  }


  
  private checkCookieConsent(): void {
    const cookieConsentResults = this.getConsentState();

    if (cookieConsentResults?.timestamp == null && this.router.url !== '/cookie-consent' && this.router.url !== '/legal/cookie-policy') {
      this.showCookieModal();
    }
  }

  private showCookieModal(): void {
    this.mbs.open(CookieConsentSheetComponent, { disableClose: true }).afterDismissed().subscribe(event => {
      this.trySetupNewRelic();
    });
  }

  private setLocaleLabel() {
    let localeCookie = this.cookieService.get(CookieNames.locale);
    if (localeCookie != null) {
      let selectedLocale = this.localesList.find(x => x.locale == localeCookie);
      if (selectedLocale != null) {
        this.currentLocaleLabel = selectedLocale.localeLabel;
      } else {
        this.currentLocaleLabel = 'EN';
      }
    } else {
      this.currentLocaleLabel = 'EN';
    }
  }

  private addSharedMetaTags() {
    let description = $localize`Effortlessly find or offer horse transportation with trusted users on Horse Hop platform. Save money and travel sustainably.`;

    this.meta.updateTag({ property: 'og:title', content: $localize`Horse Hop - Ride-sharing for equestrians` });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'description', content: description });
    this.meta.updateTag({ property: 'keywords', content: $localize`horse transport, horse transportation, equine transport, horse shipping, horse truck, horse lorry, horses, ride-sharing, find ride` });
  }

  private addMetaTagsForLocale(locale: string) {
    let mainLocale = this.localesList.find(x => x.locale === locale)!;
    if (!mainLocale) {
      //fallback option for local development
      console.warn(`Locale ${locale} not found in localesList, using default 'en' locale.`);
      mainLocale = this.localesList[0];
    }

    this.meta.addTags([
      { property: 'og:locale', content: mainLocale.ogLocale },
      { property: 'og:url', content: `https://horsehop.eu/${mainLocale.locale}/` }
    ]);

    this.localesList.filter(x => x.locale !== locale)
      .map(x => x.ogLocale)
      .forEach(locale => {
        this.meta.addTag({ property: 'og:locale:alternate', content: locale });
      });
  }
}
